<template>
	<div class="w-full bg-slate-50 dark:bg-gray-900">
		<div class="flex">
			<main class="relative flex-1 w-full">
				<div class="px-4 sm:px-6 py-6 space-y-6 max-w-7xl mx-auto">

					<!-- Back -->
					<button
						type="button"
						class="flex items-center gap-1 text-sm text-slate-400 transition"
						@click="$router.back()"
					>
						<ChevronLeft class="h-4 w-4" />
						Back
					</button>

					<!-- HERO BANNER -->
					<div v-if="profileLoading" class="rounded-2xl overflow-hidden shadow-xl bg-gray-100 dark:bg-gray-800 p-6">
						<div class="flex items-center gap-5">
							<Skeleton variant="block" w="w-24" h="h-24" class-name="rounded-full shrink-0" />
							<div class="flex-1 space-y-3">
								<Skeleton variant="block" h="h-7" w="w-48" />
								<Skeleton variant="block" h="h-4" w="w-64" />
								<Skeleton variant="block" h="h-6" w="w-28" class-name="rounded-full" />
							</div>
							<Skeleton variant="block" h="h-10" w="w-32" class-name="rounded-lg shrink-0" />
						</div>
					</div>
					<div v-else class="relative rounded-2xl overflow-hidden shadow-xl"
						style="background: linear-gradient(135deg, #4a1f61 0%, #6c328b 45%, #75a321 100%);">
						<div class="flex flex-col sm:flex-row sm:items-center gap-5 px-4 sm:px-6 py-6">
							<!-- Avatar -->
							<div class="relative shrink-0 self-center sm:self-auto">
								<div class="h-20 sm:h-24 w-20 sm:w-24 rounded-full border-4 border-white/30 overflow-hidden shadow-lg bg-white/10">
									<template v-if="hasAvatar">
										<button type="button" @click="showAvatarPreview = true" class="h-full w-full">
											<img :src="user.avatar" alt="profile" class="h-full w-full object-cover cursor-pointer hover:opacity-90 transition-opacity" />
										</button>
									</template>
									<div v-else
										class="flex h-full w-full items-center justify-center text-3xl font-bold text-white"
										:style="avatarFillStyle">
										{{ avatarInitial }}
									</div>
								</div>
								<button
									type="button"
									class="absolute bottom-0 right-0 grid h-7 w-7 place-items-center rounded-full bg-white/20 hover:bg-white/40 hover:scale-110 text-white transition-all group"
									title="Change photo"
									@click="fileInputRef?.click()"
								>
									<Camera class="h-3.5 w-3.5" />
									<span class="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black/70 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
										Change photo
									</span>
								</button>
								<input
									ref="fileInputRef"
									type="file"
									accept="image/*"
									class="hidden"
									@change="handleFileSelected"
								/>
							</div>

							<!-- Name / Email / Badge -->
							<div class="flex-1 min-w-0 text-center sm:text-left">
								<h1 class="text-xl sm:text-2xl font-bold text-white">{{ displayName }}</h1>
								<div class="flex items-center justify-center sm:justify-start gap-1.5 mt-1 text-sm text-white/80">
									<Mail class="h-3.5 w-3.5 shrink-0" />
									<span class="truncate">{{ user.email }}</span>
								</div>
								<span v-if="user.position" class="mt-2 inline-flex items-center rounded-full border border-white/40 px-3 py-0.5 text-xs font-medium text-white">
									{{ user.position}}
								</span>
							</div>

							<!-- Edit Profile Button -->
							<button
								type="button"
								class="w-full sm:w-auto shrink-0 inline-flex items-center justify-center gap-2 rounded-lg bg-white text-gray-800 font-semibold text-sm px-4 py-2 shadow hover:bg-white/90 transition"
								@click="editing.personal = true"
							>
								<Pencil class="h-3.5 w-3.5" />
								Edit Profile
							</button>
						</div>
					</div>

					<!-- TAB NAVIGATION -->
					<div v-if="profileLoading" class="flex gap-4">
						<Skeleton variant="block" h="h-10" w="w-36" class-name="rounded-lg" />
						<Skeleton variant="block" h="h-10" w="w-28" class-name="rounded-lg" />
					</div>
					<div v-else class="flex gap-1 border-b border-gray-200 dark:border-gray-700">
						<button
							type="button"
							class="px-4 py-2.5 text-sm font-semibold transition border-b-2 -mb-px"
							:class="activeTab === 'personal'
								? 'border-primary text-primary dark:text-secondary dark:border-secondary'
								: 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'"
							@click="activeTab = 'personal'"
						>
							Personal Details
						</button>
						<button
							type="button"
							class="px-4 py-2.5 text-sm font-semibold transition border-b-2 -mb-px"
							:class="activeTab === 'documents'
								? 'border-primary text-primary dark:text-secondary dark:border-secondary'
								: 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'"
							@click="activeTab = 'documents'"
						>
							Documents
						</button>
					</div>

					<!-- LOADING SKELETON -->
					<template v-if="profileLoading">
						<div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
							<Skeleton variant="card" :extra-lines="8" />
							<Skeleton variant="card" :extra-lines="4" />
						</div>
						<Skeleton variant="card" class-name="lg:w-1/2" :extra-lines="3" />
						<Skeleton variant="card" :extra-lines="4" class-name="max-w-md" />
					</template>

					<!-- PERSONAL DETAILS TAB -->
					<template v-else-if="activeTab === 'personal'">
					<!-- INFO CARDS ROW -->
					<div class="grid grid-cols-1 gap-6 lg:grid-cols-2">

						<!-- Personal Information -->
						<div class="rounded-2xl dark:bg-gray-800 border dark:border-gray-700 shadow-sm">
							<div class="flex items-center gap-2 px-6 py-4 border-b dark:border-gray-700">
								<User class="h-5 w-5 dark:text-slate-300" />
								<h2 class="text-xl font-bold dark:text-white">Personal Information</h2>
							</div>
							<div class="px-6 py-5 grid grid-cols-2 gap-x-8 gap-y-5">
								<div>
									<div class="text-sm text-slate-400">Display Name</div>
									<div class="mt-0.5 text-base font-semibold dark:text-white">{{ displayName }}</div>
								</div>
								<div>
									<div class="text-sm text-slate-400">Username</div>
									<div class="mt-0.5 text-base font-semibold dark:text-white">{{ user.username || 'Not assigned' }}</div>
								</div>
								<div>
									<div class="text-sm text-slate-400">First Name</div>
									<div class="mt-0.5 text-base font-semibold dark:text-white">{{ user.firstName || 'Not provided' }}</div>
								</div>
								<div>
									<div class="text-sm text-slate-400">Last Name</div>
									<div class="mt-0.5 text-base font-semibold dark:text-white">{{ user.lastName || 'Not provided' }}</div>
								</div>
								<div>
									<div class="text-sm text-slate-400">Email Address</div>
									<div class="mt-0.5 text-base font-semibold dark:text-white break-all">{{ user.email || 'Not provided' }}</div>
								</div>
								<div>
									<div class="text-sm text-slate-400">Phone Number</div>
									<div class="mt-0.5 text-base font-semibold dark:text-white">{{ user.phone || 'Not provided' }}</div>
								</div>
								<div class="col-span-2">
									<div class="text-sm text-slate-400">Address</div>
									<div class="mt-0.5 text-base font-semibold dark:text-white">{{ [user.address, user.city, user.state, user.zipcode].filter(Boolean).join(', ') || 'Not provided' }}</div>
								</div>
							</div>
						</div>

						<!-- Additional Info (Position / DOB / Gender / Zip) -->
						<div class="rounded-2xl dark:bg-gray-800 border dark:border-gray-700 shadow-sm">
							<div class="flex items-center gap-2 px-6 py-4 border-b dark:border-gray-700">
								<GraduationCap class="h-5 w-5 dark:text-slate-300" />
								<h2 class="text-xl font-bold dark:text-white">Additional Information</h2>
							</div>
							<div class="px-6 py-5 grid grid-cols-2 gap-x-8 gap-y-5">
								<div>
									<div class="text-sm text-slate-400">Position</div>
									<div class="mt-0.5 text-base font-semibold dark:text-white">{{ user.position || 'Not provided' }}</div>
								</div>
								<div>
									<div class="text-sm text-slate-400">Gender</div>
									<div class="mt-0.5 text-base font-semibold dark:text-white">{{ user.gender || 'Not provided' }}</div>
								</div>
								<div>
									<div class="text-sm text-slate-400">Date of Birth</div>
									<div class="mt-0.5 text-base font-semibold dark:text-white">{{ formattedDateOfBirth !== '-' ? formattedDateOfBirth : 'Not provided' }}</div>
								</div>
								<div>
									<div class="text-sm text-slate-400">Zip Code</div>
									<div class="mt-0.5 text-base font-semibold dark:text-white">{{ user.zipcode || 'Not provided' }}</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Account Info -->
					<div class="rounded-2xl dark:bg-gray-800 border dark:border-gray-700 shadow-sm lg:w-1/2">
						<div class="flex items-center gap-2 px-6 py-4 border-b dark:border-gray-700">
							<ShieldCheck class="h-5 w-5 dark:text-slate-300" />
							<h2 class="text-xl font-bold dark:text-white">Account Info</h2>
						</div>
						<div class="px-6 py-5 space-y-5">
							<div>
								<div class="text-sm text-slate-400">Username</div>
								<div class="mt-0.5 text-base font-semibold dark:text-white">{{ user.username || '-' }}</div>
							</div>
							<div>
								<div class="text-sm text-slate-400">User Type</div>
								<div class="mt-0.5 text-base font-bold dark:text-white">{{ formattedRole }}</div>
							</div>
							<div>
								<div class="text-sm text-slate-400">Account Status</div>
								<div class="mt-1 flex items-center gap-1.5">
									<span class="h-2 w-2 rounded-full" :class="user.status === 'inactive' ? 'bg-red-500' : 'bg-green-500'"></span>
									<span class="text-base font-semibold" :class="user.status === 'inactive' ? 'text-red-400' : 'text-green-400'">
										{{ user.status ? (user.status.charAt(0).toUpperCase() + user.status.slice(1)) : 'Active' }}
									</span>
								</div>
							</div>
						</div>
					</div>

					<!-- Change Password -->
					<div class="rounded-2xl dark:bg-gray-800 border dark:border-gray-700 shadow-sm">
						<div class="flex items-center gap-2 px-6 py-4 border-b dark:border-gray-700">
							<Lock class="h-5 w-5 dark:text-slate-300" />
							<h2 class="text-xl font-bold dark:text-white">Change Password</h2>
						</div>
						<div class="px-6 py-5 space-y-4 max-w-md">
							<!-- Error message -->
							<p v-if="error" class="text-sm text-red-400">{{ error }}</p>

							<!-- Current Password -->
							<div>
								<label class="text-sm text-slate-400 block mb-1.5">Current Password</label>
								<div class="relative">
									<input
										:type="showCurrentPw ? 'text' : 'password'"
										v-model="passwordForm.currentPassword"
										class="w-full rounded-lg dark:bg-gray-700 border dark:border-gray-600 dark:text-white text-sm px-4 py-2.5 pr-10 outline-none focus:border-primary transition"
										placeholder=""
									/>
									<button type="button" class="absolute right-3 top-1/2 -translate-y-1/2 dark:text-slate-400 dark:hover:text-white transition hover:text-gray-700"
										@click="showCurrentPw = !showCurrentPw">
										<EyeOff v-if="!showCurrentPw" class="h-4 w-4" />
										<Eye v-else class="h-4 w-4" />
									</button>
								</div>
							</div>

							<!-- New Password -->
							<div>
								<label class="text-sm text-slate-400 block mb-1.5">New Password</label>
								<div class="relative">
									<input
										:type="showNewPw ? 'text' : 'password'"
										v-model="passwordForm.newPassword"
										class="w-full rounded-lg dark:bg-gray-700 border dark:border-gray-600 dark:text-white text-sm px-4 py-2.5 pr-10 outline-none focus:border-primary transition"
										placeholder=""
									/>
									<button type="button" class="absolute right-3 top-1/2 -translate-y-1/2 dark:text-slate-400 dark:hover:text-white transition hover:text-gray-700"
										@click="showNewPw = !showNewPw">
										<EyeOff v-if="!showNewPw" class="h-4 w-4" />
										<Eye v-else class="h-4 w-4" />
									</button>
								</div>
							</div>

							<!-- Confirm Password -->
							<div>
								<label class="text-sm text-slate-400 block mb-1.5">Confirm New Password</label>
								<div class="relative">
									<input
										:type="showConfirmPw ? 'text' : 'password'"
										v-model="passwordForm.confirmPassword"
										class="w-full rounded-lg dark:bg-gray-700 border dark:border-gray-600 dark:text-white text-sm px-4 py-2.5 pr-10 outline-none focus:border-primary transition"
										placeholder=""
									/>
									<button type="button" class="absolute right-3 top-1/2 -translate-y-1/2 dark:text-slate-400 dark:hover:text-white transition hover:text-gray-700"
										@click="showConfirmPw = !showConfirmPw">
										<EyeOff v-if="!showConfirmPw" class="h-4 w-4" />
										<Eye v-else class="h-4 w-4" />
									</button>
								</div>
								<p v-if="passwordForm.newPassword && passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword"
									class="mt-1 text-xs text-red-400">Passwords do not match</p>
							</div>

							<button
								type="button"
								class="inline-flex items-center gap-2 rounded-lg bg-red-800 hover:bg-red-700 text-white font-semibold text-sm px-5 py-2.5 transition shadow"
								:disabled="loading"
								@click="handlePasswordSave(passwordForm)"
							>
								<Lock class="h-4 w-4" />
								Update Password
							</button>
						</div>
					</div>
					</template>

					<!-- DOCUMENTS TAB -->
					<ProfileDocumentsTab v-else />

				</div>

				<!-- Personal Details Modal -->
        <ProfilePersonalModal
          :is-open="editing.personal"
          :draft="draft"
          :loading="profileLoading"
          :positions="positions"
          @close="closeAll"
          @save="handlePersonalSave"
        />

				<!-- Email Modal -->
				<ProfileEmailModal
				:is-open="editing.email"
				:draft="draft"
				:loading="loading"
				@close="closeAll"
				@save="handleEmailSave"
				/>

				<!-- Password Modal -->
				<ProfilePasswordModal
				:is-open="editing.password"
				:password-form="passwordForm"
				:loading="loading"
				:error="error"
				@close="closeAll"
				@save="handlePasswordSave"
				/>

				<!-- Avatar Cropper Modal -->
				<Modal :model-value="editing.avatar" title="Crop Profile Photo" size="medium" @update:model-value="closeAll">
					<div v-if="avatarPreview" class="space-y-4">
						<label class="mb-1 block text-xs font-semibold text-slate-700">
							Crop your image
							<span class="font-normal text-slate-400">— drag to adjust</span>
						</label>
						<div class="rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
							<vue-cropper
							ref="cropperRef"
							:src="avatarPreview"
							:aspect-ratio="1"
							:view-mode="1"
							:guides="true"
							:auto-crop-area="0.8"
							:background="false"
							:zoomable="true"
							:rotatable="true"
							/>
						</div>
						<div class="flex items-center justify-end gap-2">
							<button type="button" class="px-3 py-1.5 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-100 transition" @click="rotateCropper(-90)">
							<RotateCcw class="w-4 h-4" />
							</button>
							<button type="button" class="px-3 py-1.5 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-100 transition" @click="rotateCropper(90)">
							<RotateCw class="w-4 h-4" />
							</button>
							<div class="flex-1" />
							<label class="px-3 py-1.5 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-100 transition cursor-pointer">
							Change Image
							<input type="file" accept="image/*" class="hidden" @change="handleFileSelected" />
							</label>
						</div>
						<p v-if="error" class="mt-1 text-xs text-red-600">{{ error }}</p>
					</div>

					<template #footer>
						<button
						type="button"
						class="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
						@click="closeAll"
						>
						Cancel
						</button>
						<button
						type="button"
						class="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-primary to-secondary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:brightness-110 transition disabled:cursor-not-allowed disabled:opacity-60"
						:disabled="loading"
						@click="handleCropAndSave"
						>
						Save
						</button>
					</template>
				</Modal>

				<!-- Avatar Preview Modal -->
				<Modal v-model="showAvatarPreview" title="Profile Photo" size="small">
					<div class="flex items-center justify-center p-2">
						<img :src="user.avatar" alt="profile" class="max-w-full max-h-[70vh] rounded-lg" />
					</div>
					<template #footer>
						<button type="button"
							class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-secondary-text dark:text-secondary-dark-text hover:bg-gray-100/80 dark:hover:bg-gray-700/60 transition-colors"
							@click="showAvatarPreview = false">Close</button>
					</template>
				</Modal>
			</main>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { User, ShieldCheck, Pencil, Lock, Camera, Mail, GraduationCap, ChevronLeft, Eye, EyeOff, RotateCcw, RotateCw } from 'lucide-vue-next'
import api from '@/services/api'
import { positionService, type Position } from '@/services/positionService'
import { useAuthStore } from '@/stores/auth'
import { getAvatarColors } from '@/utils/avatarColor'
import ProfilePersonalModal from '@/components/profile/ProfilePersonalModal.vue'
import ProfileEmailModal from '@/components/profile/ProfileEmailModal.vue'
import ProfilePasswordModal from '@/components/profile/ProfilePasswordModal.vue'
import ProfileDocumentsTab from '@/components/profile/ProfileDocumentsTab.vue'
import Modal from '@/components/layout/Modal.vue'
import VueCropper from 'vue-cropperjs'
import 'cropperjs/dist/cropper.css'
import Skeleton from '@/components/ui/Skeleton.vue'
import { useToast } from 'vue-toastification'

// Initialize toast functions
const toast = useToast()
const toastTimeout = 3000

const getErrorMessage = (err: unknown, defaultMessage: string): string => {
  const axiosError = err as { response?: { status?: number; data?: { message?: string; errors?: Record<string, string[]> } } }
  const status = axiosError.response?.status
  const data = axiosError.response?.data

  if (status === 404) {
    return 'The requested resource was not found. Please try again later.'
  }
  if (status === 403) {
    return 'You do not have permission to perform this action.'
  }
  if (status === 500) {
    return 'A server error occurred. Please try again later.'
  }
  if (status === 422 && data?.errors) {
    const firstField = Object.keys(data.errors)[0]
    const firstError = data.errors[firstField]?.[0]
    return firstError || defaultMessage
  }
  if (status === 422 && data?.message) {
    return data.message
  }
  return defaultMessage
}

interface ProfileDetails {
  phone?: string
  address?: string
  city?: string
  state?: string
  zipcode?: string
  date_of_birth?: string
  gender?: string
  profile_picture?: string
  first_name?: string
  last_name?: string
}

interface Profile {
  id: number
  name?: string
  first_name?: string
  last_name?: string
  email: string
  username?: string
  role?: string
  status?: string
  phone?: string
  address?: string
  city?: string
  state?: string
  zipcode?: string
  date_of_birth?: string
  dob?: string
  gender?: string
  profile_picture?: string
  email_verified_at?: string
  created_at?: string
  updated_at?: string
  position_id?: number
  position?: string
  details?: ProfileDetails
}

interface ProfileResponse {
  success?: boolean
  data: Profile
}

const authStore = useAuthStore()

const user = reactive({
	id: 0,
	name: '',
	firstName: '',
	lastName: '',
	username: '',
	role: '',
	status: '',
	dateOfBirth: '',
	gender: '',
	address: '',
	city: '',
	state: '',
	zipcode: '',
	phone: '',
	email: '',
	emailVerifiedAt: '',
	createdAt: '',
	updatedAt: '',
	avatar: '',
	position: ''
})

const loading = ref(false)
const profileLoading = ref(true)
const activeTab = ref<'personal' | 'documents'>('personal')
const error = ref('')
const fileInputRef = ref<HTMLInputElement | null>(null)
const showAvatarPreview = ref(false)
const cropperRef = ref<any>(null)
const avatarFile = ref<File | null>(null)
const avatarPreview = ref('')
const positions = ref<Position[]>([])
const showCurrentPw = ref(false)
const showNewPw = ref(false)
const showConfirmPw = ref(false)

const editing = reactive({
  personal: false,
  email: false,
  password: false,
  avatar: false
})

const passwordForm = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

const draft = reactive({
  firstName: '',
  lastName: '',
  username: '',
  dateOfBirth: '',
  gender: '',
  address: '',
  city: '',
  state: '',
  zipcode: '',
  phone: '',
  email: '',
  positionId: null as number | null
})

const hasAvatar = computed(() => Boolean(user.avatar))

const avatarInitial = computed(() => {
  const raw = user.firstName || user.lastName || user.username || user.name || user.email
  const initial = raw.trim().charAt(0).toUpperCase()
  return initial
})

const displayName = computed(() => {
  const combined = [user.firstName, user.lastName].filter(Boolean).join(' ')
  return combined || user.name || '-'
})

const avatarSeed = computed(() =>
  String(user.id || user.email || user.username || user.firstName || user.lastName || 'user')
)

const avatarColors = computed(() => getAvatarColors(avatarSeed.value))

const avatarFillStyle = computed(() => ({
  backgroundColor: avatarColors.value.primary
}))

const formattedDateOfBirth = computed(() => {
	if (!user.dateOfBirth) return '-'
	
	const match = user.dateOfBirth.match(/^(\d{4})-(\d{2})-(\d{2})/)
	if (!match) {
		const date = new Date(user.dateOfBirth)
		if (Number.isNaN(date.getTime())) return '-'
		return date.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'long',
		day: 'numeric'
		})
	}
	
	const [, year, month, day] = match
	const monthNames = [
		'January', 'February', 'March', 'April', 'May', 'June',
		'July', 'August', 'September', 'October', 'November', 'December'
	]
	const monthName = monthNames[parseInt(month, 10) - 1]
	return `${monthName} ${parseInt(day, 10)}, ${year}`
})

const formattedRole = computed(() => {
	const raw = (user.role || '').trim()
	if (!raw) return '-'
	return raw.charAt(0).toUpperCase() + raw.slice(1)
})

const closeAll = () => {
	editing.personal = false
	editing.email = false
	editing.password = false
	editing.avatar = false
	passwordForm.currentPassword = ''
	passwordForm.newPassword = ''
	passwordForm.confirmPassword = ''
	if (avatarPreview.value) {
		URL.revokeObjectURL(avatarPreview.value)
		avatarPreview.value = ''
	}
	error.value = ''
}

const handleFileSelected = (event: Event) => {
	const target = event.target as HTMLInputElement
	const file = target.files?.[0]
	if (!file) return

	avatarFile.value = file
	if (avatarPreview.value) {
		URL.revokeObjectURL(avatarPreview.value)
	}
	avatarPreview.value = URL.createObjectURL(file)
	error.value = ''
	editing.avatar = true

	// Reset the input so the same file can be selected again
	target.value = ''
}

watch(avatarPreview, (newVal) => {
	if (cropperRef.value && newVal) {
		cropperRef.value.replace(newVal)
	}
})

const rotateCropper = (deg: number) => {
	cropperRef.value?.rotate(deg)
}

const handleCropAndSave = async () => {
	if (!cropperRef.value) {
		error.value = 'Please choose an image to upload'
		return
	}

	const canvas = cropperRef.value.getCroppedCanvas({
		width: 300,
		height: 300,
	})
	canvas.toBlob(async (blob: Blob | null) => {
		if (blob) {
			await handleAvatarSave(blob)
		}
	}, 'image/jpeg', 0.9)
}

const getProfileName = (profile: Profile) => {
	const combined = [profile.first_name, profile.last_name].filter(Boolean).join(' ')
	return profile.name || combined
}

const normalizeGender = (value?: string) => {
	const raw = (value || '').trim().toLowerCase()
	if (!raw) return ''
	if (raw === 'male' || raw === 'm') return 'Male'
	if (raw === 'female' || raw === 'f') return 'Female'
	if (raw === 'other') return 'Other'
	if (raw === 'prefer not to say' || raw === 'prefer_not_to_say' || raw === 'prefer-not-to-say') {
		return 'Prefer not to say'
	}
	return raw.replace(/\b\w/g, (c) => c.toUpperCase())
}

const normalizeDateInput = (value?: string) => {
	const raw = (value || '').trim()
	if (!raw) return ''
	if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw
	const date = new Date(raw)
	if (Number.isNaN(date.getTime())) return ''
	const year = date.getUTCFullYear()
	const month = String(date.getUTCMonth() + 1).padStart(2, '0')
	const day = String(date.getUTCDate()).padStart(2, '0')
	return `${year}-${month}-${day}`
}

const normalizeAddress = (profile: Profile) => {
	const d = profile.details
	if (d && (d.address || d.city || d.state || d.zipcode)) {
		return {
		address: d.address ?? '',
		city: d.city ?? '',
		state: d.state ?? '',
		zipcode: d.zipcode ?? ''
		}
	}
	return {
	address: profile.address ?? '',
	city: profile.city ?? '',
	state: profile.state ?? '',
	zipcode: profile.zipcode ?? ''
	}
}

const syncUserFromProfile = (profile: Profile) => {
	const firstName = profile.first_name ?? ''
	const lastName = profile.last_name ?? ''
	const baseName = firstName || lastName ? `${firstName} ${lastName}`.trim() : (profile.name || '')

	const address = normalizeAddress(profile)
	user.id = profile.id ?? 0
	user.name = baseName
	user.firstName = firstName
	user.lastName = lastName
	user.username = profile.username ?? (profile.email?.split('@')[0] || '')
	user.role = profile.role ?? 'User'
	user.status = profile.status ?? ''
	const nextDateOfBirth = profile.details?.date_of_birth ?? profile.date_of_birth ?? profile.dob
	user.dateOfBirth = nextDateOfBirth !== undefined ? nextDateOfBirth : user.dateOfBirth
	const nextGender = profile.details?.gender ?? profile.gender
	user.gender = nextGender !== undefined ? normalizeGender(nextGender) : user.gender
	user.address = address.address
	user.city = address.city
	user.state = address.state
	user.zipcode = address.zipcode
	user.phone = profile.details?.phone ?? profile.phone ?? ''
	user.email = profile.email ?? ''
	user.emailVerifiedAt = profile.email_verified_at ?? ''
	user.createdAt = profile.created_at ?? ''
	user.updatedAt = profile.updated_at ?? ''
	user.avatar = profile.profile_picture || ''
	user.position = profile.position ?? ''

	draft.firstName = user.firstName
	draft.lastName = user.lastName
	draft.username = user.username
	draft.dateOfBirth = normalizeDateInput(user.dateOfBirth)
	draft.gender = user.gender
	draft.address = user.address
	draft.city = user.city
	draft.state = user.state
	draft.zipcode = user.zipcode
	draft.phone = user.phone
	draft.email = user.email
	draft.positionId = profile.position_id ?? null
}

const updateAuthCache = (profile: Profile) => {
	const display = getProfileName(profile)
	authStore.user = {
		id: profile.id,
		name: display,
		email: profile.email,
		role: profile.role,
		position_id: profile.position_id,
		position: profile.position,
		profile_picture: profile.profile_picture
	}

	const storage = sessionStorage.getItem('token')
		? sessionStorage
		: localStorage.getItem('token')
		? localStorage
		: null

	if (storage) {
		storage.setItem('user', JSON.stringify(authStore.user))
	}
}

const fetchProfile = async () => {
	profileLoading.value = true
	error.value = ''
	try {
		const [profileResponse, fetchedPositions] = await Promise.all([
			api.get<ProfileResponse | Profile>('/profile'),
			positionService.getPositions()
		])
		const data = (profileResponse.data as ProfileResponse).data ?? (profileResponse.data as Profile)
		positions.value = fetchedPositions
		const matched = data.position_id ? fetchedPositions.find(p => p.id === data.position_id) : null
		data.position = matched ? matched.name : ''
		syncUserFromProfile(data)
		updateAuthCache(data)
	} catch (err) {
		console.error('Profile fetch error:', err)
		error.value = 'Failed to load profile'
	} finally {
		profileLoading.value = false
	}
}

// Modal save handlers
const handlePersonalSave = async (updatedDraft: typeof draft) => {
	Object.assign(draft, updatedDraft)

	const payload: Partial<Profile> = {
		first_name: draft.firstName,
		last_name: draft.lastName,
		username: draft.username,
		address: draft.address,
		city: draft.city,
		state: draft.state,
		zipcode: draft.zipcode,
		date_of_birth: draft.dateOfBirth,
		gender: draft.gender,
		phone: draft.phone,
		position_id: draft.positionId
	}

	const loadingToast = toast.info('Updating profile...', { timeout: false })
	loading.value = true
	error.value = ''
	try {
		const response = await api.put<ProfileResponse | Profile>('/profile', payload)
		const data = (response.data as ProfileResponse).data ?? (response.data as Profile)
		syncUserFromProfile(data)
		updateAuthCache(data)
		closeAll()
		toast.dismiss(loadingToast)
		toast.success('Profile updated successfully', { timeout: toastTimeout })
		setTimeout(() => window.location.reload(), 800)
	} catch (err) {
		console.error('Profile update error:', err)
		toast.dismiss(loadingToast)
		const errorMessage = getErrorMessage(err, 'Failed to update profile')
		toast.error(errorMessage, { timeout: toastTimeout })
		error.value = errorMessage
	} finally {
		loading.value = false
	}
}

const handleEmailSave = async (updatedDraft: typeof draft) => {
	Object.assign(draft, updatedDraft)

	const payload: Partial<Profile> = {
		email: draft.email
	}

	const loadingToast = toast.info('Updating email...', { timeout: false })
	loading.value = true
	error.value = ''
	try {
		const response = await api.put<ProfileResponse | Profile>('/profile', payload)
		const data = (response.data as ProfileResponse).data ?? (response.data as Profile)
		syncUserFromProfile(data)
		updateAuthCache(data)
		closeAll()
		toast.dismiss(loadingToast)
		toast.success('Email updated successfully', { timeout: toastTimeout })
	} catch (err) {
		console.error('Email update error:', err)
		toast.dismiss(loadingToast)
		const errorMessage = getErrorMessage(err, 'Failed to update email')
		toast.error(errorMessage, { timeout: toastTimeout })
		error.value = errorMessage
	} finally {
		loading.value = false
	}
}

const handlePasswordSave = async (updatedPasswordForm: typeof passwordForm) => {
	Object.assign(passwordForm, updatedPasswordForm)

	if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
		toast.error('Please complete all password fields', { timeout: toastTimeout })
		return
	}
	if (passwordForm.newPassword.length < 8) {
		toast.error('New password must be at least 8 characters', { timeout: toastTimeout })
		return
	}
	if (passwordForm.newPassword !== passwordForm.confirmPassword) {
		toast.error('Passwords do not match', { timeout: toastTimeout })
		return
	}
	if (passwordForm.newPassword === passwordForm.currentPassword) {
		toast.error('New password must be different from current password', { timeout: toastTimeout })
		return
	}

	const loadingToast = toast.info('Changing password...', { timeout: toastTimeout })
	loading.value = true
	error.value = ''
	try {
		await api.post('/profile/password', {
			current_password: passwordForm.currentPassword,
			password: passwordForm.newPassword,
			password_confirmation: passwordForm.confirmPassword
		})
		closeAll()
		toast.dismiss(loadingToast)
		toast.success('Password changed successfully', { timeout: toastTimeout })
	} catch (err) {
		toast.dismiss(loadingToast)
		const message =
		(err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
		'Failed to change password'
		toast.error(message, { timeout: toastTimeout })
		error.value = message
	} finally {
		loading.value = false
	}
}

const handleAvatarSave = async (croppedBlob?: Blob) => {
	if (!croppedBlob) {
		error.value = 'Please choose an image to upload'
		return
	}

	const formData = new FormData()
	formData.append('avatar', croppedBlob, 'avatar.jpg')

	loading.value = true
	error.value = ''
	try {
		const config = {
			headers: { 'Content-Type': 'multipart/form-data' },
			skipGlobalError: true
		}
		const response = await api.post<ProfileResponse | Profile>('/profile/avatar', formData, config as any)
		const data = (response.data as ProfileResponse).data ?? (response.data as Profile)
		syncUserFromProfile(data)
		updateAuthCache(data)
		toast.success('Profile picture updated successfully', { timeout: toastTimeout })
		closeAll()
	} catch (err) {
		console.error('Avatar upload error:', err)
		const errorMessage = getErrorMessage(err, 'Failed to upload profile picture')
		toast.error(errorMessage, { timeout: toastTimeout })
		error.value = errorMessage
	} finally {
		loading.value = false
	}
}

onMounted(() => {
  	fetchProfile()
})

onBeforeUnmount(() => {
   	// Cleanup if needed
})
</script>

<style scoped>
/* No additional styles needed - using Tailwind classes only */
</style>